import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSalesAnalytics } from "@/hooks/useSalesAnalytics";
import { useState } from "react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar
} from "recharts";
import { 
  TrendingUp, 
  TrendingDown,
  Activity, 
  Calendar,
  Users,
  Target,
  Award,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign
} from "lucide-react";

interface PerformanceTrendsProps {
  timeFilter: string;
  departmentFilter: string;
}

export const PerformanceTrends = ({ timeFilter, departmentFilter }: PerformanceTrendsProps) => {
  const { data: salesData, isLoading } = useSalesAnalytics();
  const [trendType, setTrendType] = useState("monthly");
  const [selectedEmployee, setSelectedEmployee] = useState("all");

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-80 bg-gray-200 rounded"></div>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="space-y-6">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="bg-white/80 backdrop-blur-sm border-slate-200/60">
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="space-y-2">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="h-8 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // إنشاء بيانات الاتجاهات (محاكاة بيانات شهرية)
  const monthlyData = [
    { month: "يناير", revenue: 45000, transactions: 12, employees: 8, avgPerformance: 75 },
    { month: "فبراير", revenue: 52000, transactions: 15, employees: 8, avgPerformance: 78 },
    { month: "مارس", revenue: 48000, transactions: 13, employees: 9, avgPerformance: 72 },
    { month: "أبريل", revenue: 61000, transactions: 18, employees: 9, avgPerformance: 85 },
    { month: "مايو", revenue: 68000, transactions: 22, employees: 10, avgPerformance: 88 },
    { month: "يونيو", revenue: 74000, transactions: 25, employees: 10, avgPerformance: 92 },
  ];

  const weeklyData = [
    { week: "الأسبوع 1", revenue: 18000, transactions: 8, performance: 85 },
    { week: "الأسبوع 2", revenue: 22000, transactions: 10, performance: 88 },
    { week: "الأسبوع 3", revenue: 19000, transactions: 9, performance: 82 },
    { week: "الأسبوع 4", revenue: 25000, transactions: 12, performance: 94 },
  ];

  const currentData = trendType === "weekly" ? weeklyData : monthlyData;
  const dataKey = trendType === "weekly" ? "week" : "month";

  // حساب الاتجاهات
  const revenueGrowth = currentData.length > 1 ? 
    ((currentData[currentData.length - 1].revenue - currentData[currentData.length - 2].revenue) / currentData[currentData.length - 2].revenue) * 100 : 0;

  const transactionGrowth = currentData.length > 1 ? 
    ((currentData[currentData.length - 1].transactions - currentData[currentData.length - 2].transactions) / currentData[currentData.length - 2].transactions) * 100 : 0;

  const getPerformanceValue = (data: any) => trendType === "weekly" ? data.performance : data.avgPerformance;
  const performanceGrowth = currentData.length > 1 ? 
    (getPerformanceValue(currentData[currentData.length - 1]) - getPerformanceValue(currentData[currentData.length - 2])) : 0;

  // اتجاهات الموظفين الفرديين
  const employeeTrends = salesData?.topEmployees?.slice(0, 5).map(employee => ({
    name: employee.name,
    trend: Math.random() > 0.3 ? "up" : "down",
    change: Math.random() * 20 + 5,
    status: Math.random() > 0.4 ? "improving" : Math.random() > 0.7 ? "declining" : "stable"
  })) || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* الرسم البياني الرئيسي */}
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                اتجاهات الأداء
              </CardTitle>
              
              <div className="flex items-center gap-2">
                <Select value={trendType} onValueChange={setTrendType}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">أسبوعي</SelectItem>
                    <SelectItem value="monthly">شهري</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={currentData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey={dataKey}
                  className="text-sm"
                  tick={{ fontSize: 12 }}
                />
                <YAxis yAxisId="revenue" orientation="right" className="text-sm" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="performance" orientation="left" className="text-sm" tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value: any, name: string) => [
                    name === "revenue" ? `${value.toLocaleString('ar-SA')} ر.س` : 
                    name === "transactions" ? `${value} معاملة` : `${value}%`,
                    name === "revenue" ? "الإيرادات" : 
                    name === "transactions" ? "المعاملات" : "الأداء"
                  ]}
                />
                <Area
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#revenueGradient)"
                />
                <Line
                  yAxisId="performance"
                  type="monotone"
                  dataKey={trendType === "weekly" ? "performance" : "avgPerformance"}
                  stroke="#10B981"
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* رسم بياني للمعاملات */}
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Target className="w-5 h-5" />
              اتجاه المعاملات
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={currentData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey={dataKey} className="text-sm" tick={{ fontSize: 12 }} />
                <YAxis className="text-sm" tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: any) => [`${value} معاملة`, "عدد المعاملات"]} />
                <Bar dataKey="transactions" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      
      <div className="space-y-6">
        {/* مؤشرات النمو */}
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              مؤشرات النمو
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-green-500 rounded-lg">
                  <DollarSign className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">نمو الإيرادات</p>
                  <p className="font-bold text-slate-800">
                    {revenueGrowth > 0 ? '+' : ''}{revenueGrowth.toFixed(1)}%
                  </p>
                </div>
              </div>
              {revenueGrowth > 0 ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Activity className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">نمو المعاملات</p>
                  <p className="font-bold text-slate-800">
                    {transactionGrowth > 0 ? '+' : ''}{transactionGrowth.toFixed(1)}%
                  </p>
                </div>
              </div>
              {transactionGrowth > 0 ? (
                <TrendingUp className="w-5 h-5 text-blue-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Award className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">تحسن الأداء</p>
                  <p className="font-bold text-slate-800">
                    {performanceGrowth > 0 ? '+' : ''}{performanceGrowth.toFixed(1)} نقطة
                  </p>
                </div>
              </div>
              {performanceGrowth > 0 ? (
                <TrendingUp className="w-5 h-5 text-purple-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* اتجاهات الموظفين */}
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5" />
              اتجاهات الموظفين
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {employeeTrends.map((employee, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`p-1 rounded-full ${
                    employee.status === "improving" ? "bg-green-100" :
                    employee.status === "declining" ? "bg-red-100" : "bg-yellow-100"
                  }`}>
                    {employee.status === "improving" ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : employee.status === "declining" ? (
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-yellow-600" />
                    )}
                  </div>
                  <span className="font-medium text-slate-800 text-sm">{employee.name}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${
                    employee.status === "improving" ? "bg-green-100 text-green-700" :
                    employee.status === "declining" ? "bg-red-100 text-red-700" : 
                    "bg-yellow-100 text-yellow-700"
                  }`}>
                    {employee.status === "improving" ? "متحسن" :
                     employee.status === "declining" ? "متراجع" : "مستقر"}
                  </Badge>
                  <div className="flex items-center gap-1">
                    {employee.trend === "up" ? (
                      <TrendingUp className="w-3 h-3 text-green-500" />
                    ) : (
                      <TrendingDown className="w-3 h-3 text-red-500" />
                    )}
                    <span className={`text-xs font-medium ${
                      employee.trend === "up" ? "text-green-600" : "text-red-600"
                    }`}>
                      {employee.trend === "up" ? '+' : '-'}{employee.change.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {employeeTrends.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">لا توجد بيانات اتجاهات</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* توقعات الأداء */}
        <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              توقعات الشهر القادم
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-indigo-100">الإيرادات المتوقعة</span>
                <span className="font-bold">85,000 ر.س</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-indigo-100">المعاملات المتوقعة</span>
                <span className="font-bold">28 معاملة</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-indigo-100">معدل النمو المتوقع</span>
                <span className="font-bold">+12%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};